import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { isAuthenticated } = useAuth();
  const { fetchCart } = useCart();

  useEffect(() => {
    if (sessionId && isAuthenticated) {
      pollPaymentStatus();
    }
  }, [sessionId, isAuthenticated]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        fetchCart(); // Refresh cart (should be empty now)
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-concrete-white flex flex-col items-center justify-center px-6" data-testid="checkout-success-page">
        <p className="text-cement-grey">Invalid checkout session</p>
        <Link to="/products">
          <Button className="mt-4">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="max-w-lg w-full text-center">
        {status === 'loading' || status === 'processing' ? (
          <div>
            <div className="animate-spin w-16 h-16 border-4 border-chicago-red border-t-transparent rounded-full mx-auto mb-6" />
            <h1 className="font-anton text-3xl tracking-tighter uppercase">PROCESSING PAYMENT</h1>
            <p className="text-cement-grey mt-2">Please wait while we confirm your order...</p>
          </div>
        ) : status === 'success' ? (
          <div>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-anton text-4xl tracking-tighter uppercase">ORDER CONFIRMED!</h1>
            <p className="text-cement-grey mt-4 text-lg">
              Thank you for your purchase. Your order has been placed successfully.
            </p>
            
            {paymentData && (
              <div className="mt-8 p-6 bg-white border border-gray-200 text-left">
                <h2 className="font-bebas text-xl tracking-wider mb-4">ORDER DETAILS</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-cement-grey">Total Paid</span>
                    <span className="font-bold">${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cement-grey">Status</span>
                    <span className="font-bold text-green-600">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/profile">
                <Button className="w-full sm:w-auto bg-deep-obsidian hover:bg-deep-obsidian/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                  <Package className="w-4 h-4 mr-2" />
                  View Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-deep-obsidian uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                  Continue Shopping
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        ) : status === 'expired' ? (
          <div>
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-cement-grey" />
            </div>
            <h1 className="font-anton text-3xl tracking-tighter uppercase">SESSION EXPIRED</h1>
            <p className="text-cement-grey mt-4">
              Your checkout session has expired. Please try again.
            </p>
            <Link to="/cart">
              <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                Return to Cart
              </Button>
            </Link>
          </div>
        ) : (
          <div>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="font-anton text-3xl tracking-tighter uppercase">SOMETHING WENT WRONG</h1>
            <p className="text-cement-grey mt-4">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/cart">
              <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                Return to Cart
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
